import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import {
  IconSelfAnchoring,
  IconPerspectiveShifting,
  IconAdaptiveComm,
  IconRelationalInt,
} from '../components/PillarIcons';

const Pillars = () => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to anchor if hash is present
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const pillars = [
    {
      id: 'self-anchoring',
      title: 'Self-Anchoring',
      Icon: IconSelfAnchoring,
      tagline: 'Identity, Integrity, and the Inner Compass of Leadership',
      definition: 'Internal coherence under cultural pressure. Grounded leadership that does not fragment under difference.',
      description: 'Remaining internally stable while navigating external cultural complexity. Adapt your expression without losing your essence.',
      keyPrinciples: [
        'Clarify non-negotiable values that anchor your leadership',
        'Notice over-adjustment and approval-seeking patterns',
        'Regulate emotion under cultural tension',
        'Distinguish between adapting expression and losing essence'
      ],
      underPressure: 'You may become performative, silent, or overly agreeable to preserve belonging.',
      reflection: 'What do I refuse to trade for acceptance in culturally diverse settings?',
      quote: 'The leader who knows who they are can adapt without losing themselves.'
    },
    {
      id: 'perspective-shifting',
      title: 'Perspective-Shifting',
      Icon: IconPerspectiveShifting,
      tagline: 'Seeing Clearly Across Cultures Without Losing Your Ground',
      definition: 'The capacity to interpret meaning, not just observe behaviour. Cognitive flexibility without self-erasure.',
      description: 'Interpreting meaning in context before judging behaviour. Seeing beyond your own cultural lens without losing conviction.',
      keyPrinciples: [
        'Suspend premature judgement until context is understood',
        'Check assumptions and cultural defaults',
        'Ask questions that reveal intent and context',
        'Separate observation from interpretation'
      ],
      underPressure: 'You may label difference as incompetence, disrespect, or threat.',
      reflection: 'When behaviour feels unfamiliar, what story do I usually tell myself?',
      quote: 'Two people can observe the same event and walk away with entirely different conclusions. Neither may be dishonest—they are simply seeing through different lenses.'
    },
    {
      id: 'adaptive-communication',
      title: 'Adaptive Communication',
      Icon: IconAdaptiveComm,
      tagline: 'Expressing Clearly Across Cultures Without Losing Your Voice',
      definition: 'Translating intent so meaning lands without distortion. Adjusting expression while preserving clarity and voice.',
      description: 'Helping meaning land across cultures with clarity and dignity. Adjust tone and approach without diluting truth.',
      keyPrinciples: [
        'Translate intent, not just words',
        'Use clarity with relational care',
        'Repair misunderstanding early',
        'Adjust delivery without compromising conviction'
      ],
      underPressure: 'You may become too direct, too vague, or over-explain to be accepted.',
      reflection: 'Where does my intent most often fail to land as intended?',
      quote: 'When adaptation erases identity, it becomes performance. When adaptation preserves identity, it becomes leadership.'
    },
    {
      id: 'relational-integration',
      title: 'Relational Integration',
      Icon: IconRelationalInt,
      tagline: 'Building Trust, Influence, and Cohesion Across Cultures',
      definition: 'Creating shared space where trust can form across difference. Leadership that holds people together without forcing sameness.',
      description: 'Building trust and shared space where diverse people can contribute without erasure. Leading cohesion without forced sameness.',
      keyPrinciples: [
        'Create psychological safety across difference',
        'Bridge gaps between groups and individuals',
        'Establish shared norms explicitly',
        'Build trust through consistent, aligned behaviour'
      ],
      underPressure: 'You may allow fragmentation, silence, or unspoken division to grow.',
      reflection: 'Who feels safe to speak when I lead—and who goes quiet?',
      quote: 'Cohesion is not accidental. It is cultivated.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-[#071B3A] py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#C99316] mb-4">
            The Framework
          </p>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
            The Four Pillars of Cultural Fluency
          </h1>
          <p className="text-white/70 text-lg leading-relaxed max-w-2xl">
            These four capacities form the foundation of the Cultural Fluency Quotient (CFQ™). 
            Together, they enable leaders to navigate cultural complexity with clarity, dignity, and effectiveness.
          </p>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="border-b border-slate-200 py-6 px-6 lg:px-8 sticky top-16 bg-white z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap gap-4 justify-center">
            {pillars.map((pillar) => (
              <a
                key={pillar.id}
                href={`#${pillar.id}`}
                className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-[#071B3A] hover:bg-slate-50 rounded-lg transition-colors"
              >
                <pillar.Icon size={18} />
                {pillar.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Pillar Sections */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        {pillars.map((pillar, index) => (
          <section 
            key={pillar.id}
            id={pillar.id}
            className="mb-24 scroll-mt-32"
          >
            {/* Pillar Header */}
            <div className="flex items-start gap-6 mb-8">
              <div className="flex-shrink-0">
                <pillar.Icon size={72} />
              </div>
              <div>
                <span className="text-slate-400 text-xs font-medium tracking-wide">
                  Pillar 0{index + 1}
                </span>
                <h2 className="font-serif text-3xl font-bold text-[#071B3A] mb-2">
                  {pillar.title}
                </h2>
                <p className="text-slate-500 italic">
                  {pillar.tagline}
                </p>
              </div>
            </div>

            {/* Definition */}
            <div className="bg-slate-50 border-l-4 border-[#071B3A] p-6 mb-8">
              <p className="text-[#071B3A] font-medium">
                {pillar.definition}
              </p>
            </div>

            {/* Description */}
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              {pillar.description}
            </p>

            {/* Key Principles */}
            <div className="mb-8">
              <h3 className="font-semibold text-[#071B3A] mb-4">Key Principles</h3>
              <ul className="space-y-3">
                {pillar.keyPrinciples.map((principle, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-600">
                    <span className="w-1.5 h-1.5 bg-[#C99316] rounded-full mt-2 flex-shrink-0"></span>
                    {principle}
                  </li>
                ))}
              </ul>
            </div>

            {/* Under Pressure */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-amber-800 mb-2 text-sm uppercase tracking-wide">
                Under Pressure
              </h3>
              <p className="text-amber-900">
                {pillar.underPressure}
              </p>
            </div>

            {/* Reflection Question */}
            <div className="bg-[#071B3A] rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-[#C99316] mb-2 text-sm uppercase tracking-wide">
                Reflection
              </h3>
              <p className="text-white italic text-lg">
                {pillar.reflection}
              </p>
            </div>

            {/* Quote */}
            <blockquote className="border-l-2 border-slate-200 pl-6 text-slate-500 italic">
              "{pillar.quote}"
            </blockquote>

            {/* Divider */}
            {index < pillars.length - 1 && (
              <div className="border-t border-slate-200 mt-16"></div>
            )}
          </section>
        ))}
      </div>

      {/* CTA Section */}
      <section className="bg-[#071B3A] py-16 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-white mb-4">
            Discover Your Cultural Fluency Profile
          </h2>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Take the CFQ™ Assessment to understand your strengths and growth areas across all four pillars.
          </p>
          <Link
            to="/cfq-assessment"
            className="inline-flex items-center gap-2 bg-[#C99316] text-[#071B3A] px-8 py-4 font-semibold rounded-lg hover:bg-[#C99316]/90 transition-colors"
          >
            Take the CFQ™ Assessment
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pillars;
